import { useEffect, useState, useCallback, useMemo } from "react";
import moment from "moment";
import { useAuth } from "@/hooks/use-auth";
import { getAppointments } from "@/api/services";
import { tokenFromMeetingURL } from "@/utils/meeting";
import { cn } from "@/utils/cn";

import Header from "@/components/Header";
import AppointmentTable from "@/components/Appointments/AppointmentTable";
import Pagination from "@/components/Pagination";
import FilterBar from "@/components/Appointments/FilterBar";
import AppointmentGrid from "@/components/Appointments/AppointmentGrid";

/**
 * AppointmentsPage — astrologer's scheduled-meeting list.
 *
 * Activated as the live `/schedule/appointments` route in D5. Replaces
 * the simpler grid-only page that lived at the same route. This page
 * adds: server-side pagination, status-filter buttons + stats tiles,
 * advanced filter dropdown (date range, sort, duration), keyword
 * search, and a desktop list/grid view toggle. Mobile is always grid.
 *
 * Data shape returned from getAppointments() is already paginated;
 * we map it into the row shape that AppointmentTable / AppointmentGrid
 * expect (see those components' propTypes for the contract).
 *
 * Two id-like fields are derived per row, and they're not the same:
 *   - `id` / `reference`: raw Mongo Meeting._id, used as the React key
 *     and as the details-page URL param. Stable, never changes.
 *   - `meetingId`: the encrypted base64url join token extracted from
 *     `meeting.meetingURL`. This is what the in-call screens need as
 *     their route param — server's validateMeetingToken expects the
 *     encrypted token, not the raw _id. Confusing legacy naming; the
 *     comment exists so future-Ketan doesn't try to "fix" it.
 */

const FILTERS = ["All Appointments", "Pending", "Completed", "Cancelled", "Expired"];
const DEFAULT_ITEMS_PER_PAGE = 10;

// Stat tiles surfaced above the FilterBar. Counts come from the
// server's `data.statusCounts` (computed by computeStatusCounts on
// the backend) and fall back to local page-slice counts otherwise.
//
// Tile click → sets the matching status filter; the tile count and
// the filter must agree (clicking "Pending" must show as many rows
// as the tile shows). Expired is intentionally not surfaced as a
// tile — it's a rare bookkeeping state and lives in the FilterBar's
// 5th button for the cases where someone needs to find it.
const STAT_TILES = [
  { label: "Total", filterValue: "All Appointments", key: "total" },
  { label: "Pending", filterValue: "Pending", key: "pending" },
  { label: "Completed", filterValue: "Completed", key: "completed" },
  { label: "Cancelled", filterValue: "Cancelled", key: "cancelled" },
];

export default function AppointmentsPage() {
  const { astrologer } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [activeFilter, setActiveFilter] = useState("All Appointments");
  const [viewMode, setViewMode] = useState("list");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [paginationInfo, setPaginationInfo] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });
  // statusCounts is populated from the server's `data.statusCounts`
  // (added in D6 — see server/src/controllers/astrologer/astrologer.
  // controller.js :: computeStatusCounts). Server aggregates with the
  // current (search, date-range, mode, type, duration) filters but
  // strips `status` so tiles show the full breakdown across statuses
  // regardless of which tile is currently active. Falls back to local
  // page-slice counts if the server hasn't been redeployed yet — in
  // that mode the counts are scoped to the current page only.
  const [statusCounts, setStatusCounts] = useState(null);
  const [advancedFilters, setAdvancedFilters] = useState({
    sortBy: "newest",
    durations: "",
    startDate: undefined,
    endDate: undefined,
  });

  const fetchAppointments = useCallback(async () => {
    if (!astrologer.astrologerId || !astrologer.accessToken) return;

    setIsLoading(true);
    setError(null);

    try {
      const filters = {
        typeofmeeting: "Scheduled",
        page: currentPage,
        limit: itemsPerPage,
        ...advancedFilters,
      };

      if (activeFilter !== "All Appointments") {
        filters.status = activeFilter.toLowerCase();
      }

      if (searchTerm) {
        filters.searchTerm = searchTerm;
      }

      const { data } = await getAppointments(
        astrologer.astrologerId,
        astrologer.accessToken,
        filters
      );

      const rate = astrologer?.personalInformation?.rate || 0;

      const formattedAppointments = (data?.appointments || []).map((d) => {
        const [hour, minute] = (d.time || "0:0").split(":").map(Number);
        const durationSec = d.duration || 0;

        // Estimated earning surfaced in the grid card. We show this for
        // completed meetings only — pending/cancelled have zero duration
        // and a phantom "₹0" tile looks broken. The AstrologerEarning
        // ledger is the source of truth for actual pay, and the details
        // page fetches that. The details page labels the value as
        // "Earnings (est.)" when the ledger lookup didn't find a match.
        const estimatedEarning =
          d.status === "completed" ? `₹${Math.ceil(durationSec / 60) * rate}` : "";

        return {
          // Raw Meeting._id — used as React key + details-page URL param.
          id: d._id,
          reference: d._id,
          name: d.user?.name || "Client",
          date: moment(d.date).hour(hour).minute(minute).toDate(),
          status: d.status,
          image: d.user?.picture,
          type: d.type || "General Consultation",
          mode: d.mode || "Video/Voice",
          duration: durationSec,
          rate: `₹${rate}`,
          // Encrypted join token — used by the in-call route param.
          // See top-of-file comment for why this isn't the same as `id`.
          meetingId: tokenFromMeetingURL(d.meetingURL),
          earning: estimatedEarning,
        };
      });

      setAppointments(formattedAppointments);
      setPaginationInfo(data?.pagination || paginationInfo);
      // Prefer the server's aggregate (D6 — see computeStatusCounts in
      // server/src/controllers/astrologer/astrologer.controller.js).
      // Falls back to local page-slice counts if the server is on a
      // pre-D6 build — those counts only reflect the current page,
      // which is suboptimal but still better than blanks.
      setStatusCounts(
        data?.statusCounts || computeLocalStatusCounts(formattedAppointments)
      );
      setIsLoading(false);
    } catch (err) {
      console.error("Failed to fetch appointments:", err);
      setError("Failed to load appointments. Please try again later.");
      setIsLoading(false);
    }
  }, [
    astrologer.astrologerId,
    astrologer.accessToken,
    astrologer?.personalInformation?.rate,
    currentPage,
    itemsPerPage,
    activeFilter,
    searchTerm,
    advancedFilters,
    // paginationInfo intentionally NOT in deps — would cause an infinite
    // loop since we setPaginationInfo inside the effect body.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const handlePageChange = useCallback((page) => setCurrentPage(page), []);
  const handleItemsPerPageChange = useCallback((items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  }, []);
  const handleSearch = useCallback((query) => {
    setSearchTerm(query);
    setCurrentPage(1);
  }, []);
  const handleFilterChange = useCallback((filter) => {
    setActiveFilter(filter);
    setCurrentPage(1);
  }, []);
  const handleAdvancedFiltersChange = useCallback((filters) => {
    setAdvancedFilters(filters);
    setCurrentPage(1);
  }, []);

  // Tile counts — read off whatever statusCounts we have. When the
  // server returns proper counts, the tiles reflect the full dataset;
  // when computed locally, they reflect the current page slice only.
  // The cancelled tile counts only cancelled rows (matches the tile's
  // filter target); expired rows are surfaced via the FilterBar's 5th
  // button rather than rolled into a misleading combined tile count.
  const tileCounts = useMemo(
    () => ({
      total: statusCounts?.total ?? appointments.length,
      pending: statusCounts?.pending ?? 0,
      completed: statusCounts?.completed ?? 0,
      cancelled: statusCounts?.cancelled ?? 0,
    }),
    [statusCounts, appointments.length]
  );

  if (error) {
    return (
      <div className="flex flex-col">
        <Header name="My Appointments" />
        <div className="flex justify-center items-center w-full h-64 px-6">
          <p className="text-red-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <Header name="My Appointments" />

      <div className="flex flex-col gap-4 px-6 mt-4 relative">
        {/* Stats tiles — click to apply that filter. */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {STAT_TILES.map((tile) => {
            const isActive = activeFilter === tile.filterValue;
            const count = tileCounts[tile.key];
            return (
              <button
                key={tile.key}
                onClick={() => handleFilterChange(tile.filterValue)}
                aria-pressed={isActive}
                className={cn(
                  "p-3 rounded-xl text-center transition-colors shadow-sm",
                  isActive
                    ? "bg-orange-500 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                )}
              >
                <p className="text-2xl font-bold">{count}</p>
                <p className="text-xs uppercase tracking-wider opacity-80">
                  {tile.label}
                </p>
              </button>
            );
          })}
        </div>

        <FilterBar
          filters={FILTERS}
          activeFilter={activeFilter}
          setActiveFilter={handleFilterChange}
          viewMode={viewMode}
          setViewMode={setViewMode}
          onSearch={handleSearch}
          onAdvancedFiltersChange={handleAdvancedFiltersChange}
        />

        {isLoading ? (
          <div className="flex justify-center items-center w-full h-64">
            <p className="text-gray-500">Loading appointments…</p>
          </div>
        ) : (
          <>
            {appointments.length === 0 ? (
              <div className="flex justify-center items-center w-full h-64">
                <p className="text-gray-500">
                  No appointments found matching your criteria.
                </p>
              </div>
            ) : (
              <>
                {/* Mobile View — always Grid. */}
                <div className="block md:hidden w-full">
                  <AppointmentGrid paginatedData={appointments} />
                </div>

                {/* Desktop View — respect user preference. */}
                <div className="hidden md:block w-full">
                  {viewMode === "grid" ? (
                    <AppointmentGrid paginatedData={appointments} />
                  ) : (
                    <AppointmentTable paginatedData={appointments} />
                  )}
                </div>

                <Pagination
                  totalItems={paginationInfo.totalItems}
                  itemsPerPage={itemsPerPage}
                  currentPage={paginationInfo.currentPage}
                  totalPages={paginationInfo.totalPages}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                  hasNextPage={paginationInfo.hasNextPage}
                  hasPrevPage={paginationInfo.hasPrevPage}
                />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Compute status-grouped counts from the current page of appointments.
 * Used as a fallback when the server doesn't return aggregate counts.
 * Best-effort: when filtered, counts reflect the filtered slice not
 * the full dataset.
 */
function computeLocalStatusCounts(rows) {
  const counts = { total: rows.length, pending: 0, completed: 0, cancelled: 0, expired: 0 };
  for (const r of rows) {
    if (r.status in counts) counts[r.status] = (counts[r.status] || 0) + 1;
  }
  return counts;
}
